import { FC, FocusEvent } from 'react';
import { Delete } from '@mui/icons-material';
import { Alert, Button, IconButton, MenuItem } from '@mui/material';
import { Field, FieldArray, useField, useFormikContext } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Section, SectionTitle } from '..';
import { InputField } from '../../../fields';
import { pageUrlSchema } from '../../../../lib';
import { matchTypes, PageRule } from '../../../../types';
import { t } from 'i18next';

type RulesProps = {
  sliceName: string;
};

const Rules: FC<RulesProps> = ({ sliceName }) => {
  const [field] = useField<PageRule[]>(sliceName);
  const { submitForm, setFieldValue } = useFormikContext();

  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  return (
    <FieldArray name={sliceName}>
      {({ remove, push }) => {
        return (
          <div>
            {Boolean(field.value?.length) &&
              field.value.map((_, index) => {
                const valueFieldName = `${sliceName}[${index}].value`;

                return (
                  <RuleWrapper key={index}>
                    <Field
                      name={`${sliceName}[${index}].matchType`}
                      component={InputField}
                      label="Match type"
                      select
                      className="match-type-field"
                    >
                      {matchTypes.map((matchType) => (
                        <MenuItem key={matchType} value={matchType}>
                          {matchType}
                        </MenuItem>
                      ))}
                    </Field>

                    <Field
                      name={valueFieldName}
                      component={InputField}
                      label="Relative page url"
                      helperText="Should be relative and starts with a '/'"
                      className="value-type-field"
                      onBlur={(event: FocusEvent<HTMLInputElement>) => {
                        let value = event.target.value;

                        if (!value) {
                          return;
                        }

                        try {
                          const url = new URL(value);
                          value = url.pathname;
                        } catch (error) {
                          if (!value.startsWith('/')) {
                            value = `/${value}`;
                          }
                        }

                        if (value.endsWith('/')) {
                          value = value.slice(0, value.length - 1);
                        }

                        setFieldValue(valueFieldName, value).then(() => {
                          submitForm();
                        });
                      }}
                      validate={(value: string) => validate(value, pageUrlSchema)}
                      submitOnBlur={false}
                    />

                    <IconButton
                      title="Remove rule"
                      onClick={() => {
                        remove(index);
                        submitForm();
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </RuleWrapper>
                );
              })}

            <AddRuleButton
              onClick={() => {
                push({
                  matchType: 'equal',
                  value: '/page-1',
                });
                submitForm();
              }}
            >
              {t('pages.main.buttons.addRule')}
            </AddRuleButton>
          </div>
        );
      }}
    </FieldArray>
  );
};

export const PagesToShow = () => {
  const includePagesSliceName = 'campaign.displayRules.pages.include';
  const { t } = useTranslation();

  const [field] = useField<PageRule[]>(includePagesSliceName);

  return (
    <Section elevation={0}>
      <SectionRow>
        <SectionTitle>{t('pages.campaign.showOnPages')}</SectionTitle>

        {field.value.length === 0 ? (
          <Alert severity="info" variant="filled">
            {t('pages.campaign.displayedOnPagesInfo')}
            <Rules sliceName={includePagesSliceName} />
          </Alert>
        ) : (
          <Rules sliceName={includePagesSliceName} />
        )}
      </SectionRow>

      <SectionRow>
        <SectionTitle>{t('pages.campaign.dontShowOnPages')}</SectionTitle>
        <Rules sliceName="campaign.displayRules.pages.exclude" />
      </SectionRow>
    </Section>
  );
};

const AddRuleButton = styled(Button)`
  padding-left: 4px;
`;

const RuleWrapper = styled.div`
  margin-bottom: 16px;

  .match-type-field {
    width: 120px;
    margin-right: 12px;
    min-width: unset;
  }

  .value-type-field {
    margin-right: 4px;
  }

  :last-of-type {
    margin-bottom: 0;
  }
`;

const SectionRow = styled.div`
  margin-bottom: 32px;

  :last-child {
    margin-bottom: 0;
  }
`;
